import React, { useMemo, useState, useEffect } from 'react';
import MCForm from './forms/MCForm';
import QAForm from './forms/QAForm';
import RatingForm from './forms/RatingForm';
import WordCloudForm from './forms/WordCloudForm';
import OpenTextForm from './forms/OpenTextForm';
import RankingForm from './forms/RankingForm';
import QuizForm from './forms/QuizForm';
import SpinnerWheelForm from './forms/SpinnerWheelForm';
import CorrectOrderForm from './forms/CorrectOrderForm';
import CategoriesForm from './forms/CategoriesForm';
import RatingScaleForm from './forms/RatingScaleForm';
import ImageChoiceForm from './forms/ImageChoiceForm';
import SliderForm from './forms/SliderForm';
import NumericInputForm from './forms/NumericInputForm';
import ClickImageForm from './forms/ClickImageForm';
import BudgetAllocationForm from './forms/BudgetAllocationForm';
import ReactionForm from './forms/ReactionForm';
import PairMatchingForm from './forms/PairMatchingForm';
import PredictionForm from './forms/PredictionForm';
import TimelineOrderForm from './forms/TimelineOrderForm';
import HotspotForm from './forms/HotspotForm';
import DebateForm from './forms/DebateForm';
import GuessNumberForm from './forms/GuessNumberForm';
import SpeedQuizForm from './forms/SpeedQuizForm';
import DrawingForm from './forms/DrawingForm';
import ScratchCardForm from './forms/ScratchCardForm';
import SlotMachineForm from './forms/SlotMachineForm';
import styles from './JoinPage.module.css';

const FORM_MAP = {
    'multiple-choice': MCForm,
    'audience-qa': QAForm,
    'rating': RatingForm,
    'word-cloud': WordCloudForm,
    'open-text': OpenTextForm,
    'ranking': RankingForm,
    'quiz': QuizForm,
    'spinner-wheel': SpinnerWheelForm,
    'correct-order': CorrectOrderForm,
    'categories': CategoriesForm,
    'rating-scale': RatingScaleForm,
    'image-choice': ImageChoiceForm,
    'slider': SliderForm,
    'numeric-input': NumericInputForm,
    'click-image': ClickImageForm,
    'budget-allocation': BudgetAllocationForm,
    'reaction': ReactionForm,
    'pair-matching': PairMatchingForm,
    'prediction': PredictionForm,
    'timeline-order': TimelineOrderForm,
    'hotspot': HotspotForm,
    'debate': DebateForm,
    'guess-number': GuessNumberForm,
    'speed-quiz': SpeedQuizForm,
    'drawing': DrawingForm,
    'scratch-card': ScratchCardForm,
    'slot-machine': SlotMachineForm,
};

const TYPE_LABELS = {
    'multiple-choice': 'Multiple Choice',
    'audience-qa': 'Audience Q&A',
    'rating': 'Rating',
    'word-cloud': 'Word Cloud',
    'open-text': 'Open Text',
    'ranking': 'Ranking',
    'quiz': 'Quiz',
    'spinner-wheel': 'Spinner Wheel',
    'correct-order': 'Correct Order',
    'categories': 'Categories',
    'rating-scale': 'Rating Scale',
    'image-choice': 'Image Choice',
    'slider': 'Slider',
    'numeric-input': 'Numeric Input',
    'click-image': 'Click on Image',
    'budget-allocation': 'Budget Allocation',
    'reaction': 'Reaction',
    'pair-matching': 'Pair Matching',
    'prediction': 'Prediction',
    'timeline-order': 'Timeline Order',
    'hotspot': 'Hotspot',
    'debate': 'Debate',
    'guess-number': 'Guess the Number',
    'speed-quiz': 'Speed Quiz',
    'drawing': 'Drawing',
    'scratch-card': 'Scratch Card',
    'slot-machine': 'Slot Machine',
};

import { BarChart2, Hourglass, CheckCircle2, XCircle, Check, Pencil } from 'lucide-react';
import ResultsView from '../../components/activities/ResultsView';
import formStyles from './forms/form.module.css';

// Helper function to check if user answer is correct
const checkUserAnswerCorrectness = (userAnswer, content, activityType) => {
    if (!userAnswer || !content) return null;
    if (activityType !== 'multiple-choice' && activityType !== 'quiz') return null;
    
    const showCorrect = content?.settings?.showCorrectAnswer;
    if (!showCorrect) return null;
    
    const correctIndices = new Set(content?.correctOptions || []);
    if (correctIndices.size === 0) return null; // No correct answer marked yet
    
    const userSelections = new Set();
    const sel = userAnswer?.selected;
    const arr = Array.isArray(sel) ? sel : (sel != null ? [sel] : []);
    arr.forEach(i => userSelections.add(i));
    
    if (userSelections.size === 0) return null; // User didn't select anything
    
    const multipleMode = content?.settings?.multipleOptions;
    if (multipleMode) {
        return userSelections.size === correctIndices.size && 
               Array.from(userSelections).every(i => correctIndices.has(i));
    } else {
        return userSelections.size === 1 && Array.from(userSelections)[0] === Array.from(correctIndices)[0];
    }
};

// Normalize activity.content to an object (API/socket may send string for JSONB)
function getContent(activity) {
    const raw = activity?.content;
    if (raw == null) return null;
    if (typeof raw === 'object') return raw;
    if (typeof raw === 'string') {
        try {
            return JSON.parse(raw);
        } catch {
            return null;
        }
    }
    return null;
}

const ParticipantExperience = ({
    step, // 'waiting' | 'activity' | 'submitted'
    name,
    event,
    activity,
    responses = [],
    userAnswer = null,
    onSubmit,
    onEditResponse,
    onReact,
    onReply,
    onReplyReact,
    isPreview = false
}) => {
    const content = useMemo(() => getContent(activity), [activity?.content]);
    const FormComponent = activity ? FORM_MAP[activity.type] : null;
    const allowUpdateResponse = content?.settings?.allowUpdateResponse !== false;
    const showResultsTab = content?.settings?.pollResults === 'visible';
    const [activeTab, setActiveTab] = useState('questions');
    const questionText = content?.question || content?.prompt || '';
    const typeLabel = activity?.type ? (TYPE_LABELS[activity.type] || activity.type.replace(/-/g, ' ')) : '';

    useEffect(() => {
        setActiveTab('questions');
    }, [activity?.id]);

    return (
        <div
            className={`${styles.card} ${isPreview ? styles.previewCard : ''}`}
            style={{ margin: 0, width: '100%', maxWidth: 'none', boxShadow: 'none', animation: 'none' }}
        >
            {/* ── Step: Waiting for activity ─────────── */}
            {step === 'waiting' && (
                <div className={styles.waiting}>
                    <div className={styles.waitingEmoji}><Hourglass size={32} aria-hidden /></div>
                    <h2 className={styles.waitingTitle}>Welcome, {name || 'Participant'}!</h2>
                    <p className={styles.waitingText} role="status" aria-live="polite">
                        You're in <strong>{event?.name}</strong>.<br />
                        Waiting for the host to start an activity…
                    </p>
                    <div className={styles.pulseDots} aria-hidden="true">
                        <span /><span /><span />
                    </div>
                </div>
            )}

            {/* ── Step: Activity form with optional Questions | Results tabs ────────────────── */}
            {step === 'activity' && FormComponent && (
                <div className={styles.activityWrap}>
                    <div className={styles.activityMeta}>
                    
                        <span className={styles.eventName} title='Event Name'>{event?.name}</span>
                        
                    </div>
                    {showResultsTab ? (
                        <>
                            <div className={styles.activityTabs} role="tablist" aria-label="Questions and Results">
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'questions'}
                                    aria-controls="participant-questions-panel"
                                    id="participant-questions-tab"
                                    className={`${styles.activityTab} ${activeTab === 'questions' ? styles.activityTabActive : ''}`}
                                    onClick={() => setActiveTab('questions')}
                                >
                                    Questions
                                </button>
                                <button
                                    type="button"
                                    role="tab"
                                    aria-selected={activeTab === 'results'}
                                    aria-controls="participant-results-panel"
                                    id="participant-results-tab"
                                    className={`${styles.activityTab} ${activeTab === 'results' ? styles.activityTabActive : ''}`}
                                    onClick={() => setActiveTab('results')}
                                >
                                    Results
                                </button>
                            </div>
                            <div
                                id="participant-questions-panel"
                                role="tabpanel"
                                aria-labelledby="participant-questions-tab"
                                hidden={activeTab !== 'questions'}
                                className={styles.activityTabPanel}
                            >
                                {questionText && (
                            <div className={styles.activityTitleRow}>
                                 
                                {typeLabel && (
                                    <span title='Activity Type' className={styles.activityTypeBadge}>{typeLabel}</span>
                                )}
                            </div>
                        )}
                                <FormComponent
                                    content={content || {}}
                                    onSubmit={onSubmit}
                                    isPreview={isPreview}
                                    isLocked={content?.settings?.isLocked}
                                    userAnswer={userAnswer}
                                    allowUpdateResponse={allowUpdateResponse}
                                    eventCode={event?.code}
                                    activityId={activity?.id}
                                />
                                {userAnswer != null && allowUpdateResponse && (
                                    <p className={styles.submittedHint}>
                                        Response submitted. You can change your answer above and click &quot;Update response&quot; to resubmit.
                                    </p>
                                )}
                                {activity?.type === 'audience-qa' && content?.settings?.moderation && userAnswer != null && (
                                    <p className={styles.submittedHint} style={{ marginTop: 'var(--space-2)' }}>
                                        Your question is pending approval. It will appear once the host approves it.
                                    </p>
                                )}
                            </div>
                            <div
                                id="participant-results-panel"
                                role="tabpanel"
                                aria-labelledby="participant-results-tab"
                                hidden={activeTab !== 'results'}
                                className={styles.activityTabPanel}
                            >
                                <div className={styles.resultsReveal}>
                                    <div className={styles.resultsSectionHeader}>
                                        <BarChart2 size={18} className={styles.resultsSectionIcon} aria-hidden />
                                        <span className={styles.resultsSectionLabel}>Live Results</span>
                                        <span className={styles.resultsSectionCount}>
                                            {responses?.length ?? 0} response{(responses?.length ?? 0) !== 1 ? 's' : ''}
                                        </span>
                                    </div>
                                    <ResultsView
                                        type={activity.type}
                                        responses={responses}
                                        userAnswer={userAnswer}
                                        content={content}
                                        isParticipantView={true}
                                        onReact={onReact}
                                        onReply={onReply}
                                        onReplyReact={onReplyReact}
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <FormComponent
                                content={content || {}}
                                onSubmit={onSubmit}
                                isPreview={isPreview}
                                isLocked={content?.settings?.isLocked}
                                userAnswer={userAnswer}
                                allowUpdateResponse={allowUpdateResponse}
                                eventCode={event?.code}
                                activityId={activity?.id}
                            />
                            {userAnswer != null && allowUpdateResponse && (
                                <p className={styles.submittedHint}>
                                    Response submitted. You can change your answer above and click &quot;Update response&quot; to resubmit.
                                </p>
                            )}
                            {activity?.type === 'audience-qa' && content?.settings?.moderation && userAnswer != null && (
                                <p className={styles.submittedHint} style={{ marginTop: 'var(--space-2)' }}>
                                    Your question is pending approval. It will appear once the host approves it.
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── Step: Submitted ────────────────────── */}
            {step === 'submitted' && (
                <div className={styles.waiting} style={{ paddingBottom: 'var(--space-12)' }}>
                    {(() => {
                        const isCorrect = checkUserAnswerCorrectness(userAnswer, content, activity?.type);

                        if (isCorrect === true) {
                            return (
                                <div className={formStyles.confirmationWrap} role="status" aria-live="polite">
                                    <div className={styles.stickerWrap}>
                                        <span className="sticker-badge">
                                            <CheckCircle2 size={14} aria-hidden /> Correct!
                                        </span>
                                    </div>
                                    <p className={styles.waitingText}>Great job! You selected the right answer.</p>
                                </div>
                            );
                        } else if (isCorrect === false) {
                            return (
                                <div className={`${formStyles.resultBanner} ${formStyles.incorrect}`} role="status" aria-live="polite">
                                    <XCircle size={28} className={formStyles.confirmationIcon} style={{ color: 'var(--error)' }} aria-hidden />
                                    <h2 className={formStyles.resultBannerTitle}>Wrong answer</h2>
                                    <p className={formStyles.resultBannerText}>Check the correct answer below and learn from it.</p>
                                </div>
                            );
                        } else {
                            return (
                                <div className={formStyles.confirmationWrap} role="status" aria-live="polite">
                                    <div className={styles.stickerWrap}>
                                        <span className="sticker-badge">
                                            <Check size={14} aria-hidden /> Submitted!
                                        </span>
                                    </div>
                                    <h2 className={styles.waitingTitle}>Answer submitted</h2>
                                </div>
                            );
                        }
                    })()}

                    {content?.settings?.pollResults === 'visible' ? (
                        <div className={styles.resultsReveal}>
                            <div className={styles.resultsSectionHeader}>
                                <BarChart2 size={18} className={styles.resultsSectionIcon} aria-hidden />
                                <span className={styles.resultsSectionLabel}>Live Results</span>
                                <span className={styles.resultsSectionCount}>
                                    {responses?.length ?? 0} response{(responses?.length ?? 0) !== 1 ? 's' : ''}
                                </span>
                            </div>
                            <ResultsView
                                type={activity.type}
                                responses={responses}
                                userAnswer={userAnswer}
                                content={content}
                                isParticipantView={true}
                                onReact={onReact}
                                onReply={onReply}
                                onReplyReact={onReplyReact}
                            />
                        </div>
                    ) : (
                        <p className={styles.waitingText}>Waiting for the next activity…</p>
                    )}

                    {allowUpdateResponse && !content?.settings?.isLocked && (
                        <button
                            type="button"
                            className={styles.editBtn}
                            onClick={onEditResponse}
                        >
                            <Pencil size={14} aria-hidden /> Edit response
                        </button>
                    )}

                    {content?.settings?.pollResults !== 'visible' && (
                        <div className={styles.pulseDots} style={{ marginTop: 'var(--space-8)' }} aria-hidden="true">
                            <span /><span /><span />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default ParticipantExperience;
